import ChartGreen from "@/assets/img/today/moon/chart_icon_green.webp"
import ChartOrange from "@/assets/img/today/moon/chart_icon_orange.webp"
import ChartRed from "@/assets/img/today/moon/chart_icon_red.webp"

import { type LifestyleCard } from "../../config/moon-content"
import type { MoonLifestyleItem } from "../../types"

const LABEL_STYLE: Record<
  LifestyleCard["level"],
  { wrap: string; text: string; icon: string; level: string }
> = {
  low: { wrap: "bg-peach/10", text: "text-peach", icon: ChartRed, level: "LOW" },
  medium: { wrap: "bg-gold/10", text: "text-gold", icon: ChartOrange, level: "MEDIUM" },
  high: { wrap: "bg-green/10", text: "text-green", icon: ChartGreen, level: "HIGH" },
}

interface DailyMessageProps {
  affirmation?: {
    title: string
    description: string
  }
  lifestyle?: MoonLifestyleItem[]
}

const toCard = (item: MoonLifestyleItem): LifestyleCard => ({
  title: item.sphere.charAt(0).toUpperCase() + item.sphere.slice(1),
  description: item.description,
  level: item.energy_level.toLowerCase() as LifestyleCard["level"],
  action: item.action.toLowerCase(),
})

/** Daily affirmation + lifestyle slider. Ported from MoonPhase DailyMessage. */
export function DailyMessage({ affirmation, lifestyle }: DailyMessageProps) {
  const cards = (lifestyle ?? []).map(toCard)

  return (
    <div className="mt-10 w-full">
      {affirmation && (
        <div className="flex flex-col gap-4 px-4">
          <h3 className="font-display text-2xl font-medium text-purple-light">
            “{affirmation.title}”
          </h3>
          <p className="font-body text-base text-purple-light/70">{affirmation.description}</p>
        </div>
      )}

      <div className="mt-6 flex items-stretch gap-4 overflow-x-auto px-4 [scrollbar-width:none]">
        {cards.map((card) => (
          <div
            key={card.title}
            className="flex w-80 min-w-80 flex-col gap-4 rounded-3xl bg-gradient-to-bl from-purple-light/10 to-purple-light/5 px-4 pb-6 pt-4"
          >
            <div className="flex flex-col items-start gap-2">
              <h4 className="font-display text-xl font-medium text-purple-light">{card.title}</h4>
              <p className="font-body text-base text-purple-light/70">{card.description}</p>
            </div>
            <div className="mt-auto">
              <LifestyleLabel card={card} />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function LifestyleLabel({ card }: { card: LifestyleCard }) {
  const style = LABEL_STYLE[card.level]
  return (
    <div className={`flex w-fit items-center gap-2.5 rounded-full px-4 py-1.5 ${style.wrap}`}>
      <img src={style.icon} alt="" className="size-[30px] min-w-[30px]" />
      <div className={`flex flex-col leading-none ${style.text}`}>
        <span className="font-body text-[10px]">Moon energy</span>
        <span className="whitespace-nowrap font-body text-xs font-medium uppercase">
          {style.level} • {card.action}
        </span>
      </div>
    </div>
  )
}
